import { useEffect, useState } from 'react';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  EmptyState,
  Input,
  useToast,
} from '@adop/ui';
import { CalendarClock } from 'lucide-react';
import { DEFAULT_RESERVATION_SETTINGS } from '@adop/shared';
import { useWorkspace } from '../../lib/workspace-context';
import { useAsync } from '../../lib/useAsync';
import { getLocation, getReservationSettings, updateLocation, saveReservationSettings } from '../../lib/data';
import { errorMessage } from '../../lib/api';
import { Alert } from '../../components/Alert';
import { PageHeader } from '../../components/PageHeader';
import { LoadingBlock } from '../../components/LoadingState';

interface SettingsForm {
  enabled: boolean;
  minParty: number;
  maxParty: number;
  slotMinutes: number;
  maxDaysAhead: number;
  minNotice: number;
  autoPrint: boolean;
}

export function ReservationSettingsPage() {
  const { location: workspaceLocation } = useWorkspace();
  const { toast } = useToast();
  const state = useAsync(async () => {
    if (!workspaceLocation) return null;
    const [location, settings] = await Promise.all([
      getLocation(workspaceLocation.id),
      getReservationSettings(workspaceLocation.id),
    ]);
    if (!location) return null;
    return { location, settings };
  }, [workspaceLocation?.id]);

  const [form, setForm] = useState<SettingsForm>({
    enabled: false,
    minParty: DEFAULT_RESERVATION_SETTINGS.min_party,
    maxParty: DEFAULT_RESERVATION_SETTINGS.max_party,
    slotMinutes: DEFAULT_RESERVATION_SETTINGS.slot_minutes,
    maxDaysAhead: DEFAULT_RESERVATION_SETTINGS.max_days_ahead,
    minNotice: DEFAULT_RESERVATION_SETTINGS.min_notice_minutes,
    autoPrint: DEFAULT_RESERVATION_SETTINGS.auto_print,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!state.data) return;
    const { location, settings } = state.data;
    setForm({
      enabled: location.reservations_enabled,
      minParty: settings?.min_party ?? DEFAULT_RESERVATION_SETTINGS.min_party,
      maxParty: settings?.max_party ?? DEFAULT_RESERVATION_SETTINGS.max_party,
      slotMinutes: settings?.slot_minutes ?? DEFAULT_RESERVATION_SETTINGS.slot_minutes,
      maxDaysAhead: settings?.max_days_ahead ?? DEFAULT_RESERVATION_SETTINGS.max_days_ahead,
      minNotice: settings?.min_notice_minutes ?? DEFAULT_RESERVATION_SETTINGS.min_notice_minutes,
      autoPrint: settings?.auto_print ?? DEFAULT_RESERVATION_SETTINGS.auto_print,
    });
  }, [state.data]);

  if (!workspaceLocation) {
    return <EmptyState icon={<CalendarClock className="h-6 w-6" />} title="Select a location" />;
  }
  if (state.loading || !state.data) {
    return <LoadingBlock />;
  }

  const data = state.data;

  const save = async () => {
    const { location } = data;
    if (form.minParty < 1 || form.maxParty < form.minParty) {
      setError('Max party size must be at least the minimum.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await updateLocation(location.id, { reservations_enabled: form.enabled });
      await saveReservationSettings(
        { restaurantId: location.restaurant_id, locationId: location.id },
        {
          min_party: form.minParty,
          max_party: form.maxParty,
          slot_minutes: form.slotMinutes,
          max_days_ahead: form.maxDaysAhead,
          min_notice_minutes: form.minNotice,
          auto_print: form.autoPrint,
        },
      );
      toast({ title: 'Reservation settings saved', variant: 'success' });
      state.reload();
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5">
      <PageHeader title="Reservation settings" description="Control available times and party sizes." />

      {error ? <Alert>{error}</Alert> : null}

      <Card>
        <CardHeader>
          <CardTitle>Reservations</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Checkbox
            label="Accept table reservations"
            checked={form.enabled}
            onChange={(event) => setForm({ ...form, enabled: event.target.checked })}
          />
          <div className="grid gap-4 sm:grid-cols-2">
            <Input
              label="Minimum party size"
              type="number"
              min={1}
              value={form.minParty}
              onChange={(event) => setForm({ ...form, minParty: Number(event.target.value) })}
            />
            <Input
              label="Maximum party size"
              type="number"
              min={1}
              value={form.maxParty}
              onChange={(event) => setForm({ ...form, maxParty: Number(event.target.value) })}
            />
            <Input
              label="Slot length (minutes)"
              type="number"
              min={5}
              step={5}
              value={form.slotMinutes}
              onChange={(event) => setForm({ ...form, slotMinutes: Number(event.target.value) })}
            />
            <Input
              label="Book up to (days ahead)"
              type="number"
              min={1}
              value={form.maxDaysAhead}
              onChange={(event) => setForm({ ...form, maxDaysAhead: Number(event.target.value) })}
            />
            <Input
              label="Minimum notice (minutes)"
              type="number"
              min={0}
              step={5}
              value={form.minNotice}
              onChange={(event) => setForm({ ...form, minNotice: Number(event.target.value) })}
            />
          </div>
          <Checkbox
            label="Auto-print reservation tickets"
            checked={form.autoPrint}
            onChange={(event) => setForm({ ...form, autoPrint: event.target.checked })}
          />
          <Button loading={saving} onClick={() => void save()}>
            Save settings
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
